import { useEffect, useRef } from "react";
import { Sparkles, Mic, Lightbulb, ChevronDown } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { InfoTip } from "@/components/InfoTip";
import { PricingBadge } from "@/components/CapabilityBadge";
import { CopilotStartersCard } from "@/components/CopilotStartersCard";
import { LastChanged, useOpenState, wantedSection } from "@/components/SettingsSection";
import { V } from "@/lib/vocab";

const SECTION_ID = "copilot-section";
const voiceModeOf = (t) => t.copilot_voice_mode || (t.copilot_voice_enabled ? "dictation" : "off");

// Settings → Capabilities: the ONE Ask Copilot card. On/off like every other module, and its own
// settings (voice · feature requests · who can use it · starters) expand inside the same card.
export function CopilotModuleCard({ t, isSuper, canEditStarters, saveConfig, last }) {
  const on = t.copilot_enabled === true;
  const [open, toggle] = useOpenState(SECTION_ID);
  const ref = useRef(null);
  useEffect(() => {
    if (wantedSection() === SECTION_ID && ref.current) setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 250);
  }, []);
  const showSettings = isSuper || on;
  return (
    <div ref={ref} data-testid="module-card-copilot"
      className={`bg-white rounded-xl border border-sand-200 transition-opacity ${on ? "" : "opacity-60"} ${wantedSection() === SECTION_ID ? "ring-2 ring-redline-500/30" : ""}`}>
      <div className="p-4 flex items-start gap-3">
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${on ? "bg-redline-50" : "bg-sand-100"}`}>
          <Sparkles className={`h-[18px] w-[18px] ${on ? "text-redline-500" : "text-ink-400"}`} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-[15px] flex flex-wrap items-center gap-x-2 gap-y-1">Ask Copilot
            <PricingBadge addonKey="copilot" />
            <InfoTip>A ChatGPT-style workspace in this {V.lc("org")}'s sidebar where managers and {V.lcs("board")} ask questions grounded in the documents, rules and live operational data. Off hides it from the menu for everyone.</InfoTip>
          </div>
          <div className="text-sm text-muted-foreground mt-0.5">{showSettings ? `A manager and ${V.grp()} assistant that answers from this ${V.lc("org")}'s documents, rules and live data — by text or voice.` : `Contact Redline to enable Ask Copilot for this ${V.lc("org")}.`}</div>
          {showSettings && (
            <button type="button" data-testid="copilot-settings-toggle" aria-expanded={open} onClick={toggle}
              className="pointer-events-auto mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-redline-600 hover:text-redline-700">
              {isSuper ? "Voice · feature requests · who can use it · starters" : "Starters"}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
            </button>
          )}
          <LastChanged entry={last("copilot_enabled")} testid="last-changed-copilot" />
        </div>
        <Switch data-testid="module-copilot" disabled={!isSuper} checked={on} onCheckedChange={(v) => saveConfig({ copilot_enabled: v })} />
      </div>
      {showSettings && open && (
        <div className="pointer-events-auto border-t border-sand-100 px-4 pb-4 pt-3 space-y-3" data-testid="copilot-settings-body">
          {isSuper && <CopilotDials t={t} on={on} saveConfig={saveConfig} />}
          <div className={`rounded-2xl border border-sand-200 p-4 ${!on ? "opacity-60" : ""}`} data-testid="copilot-starters-block">
            <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground mb-2">Starters — what the welcome screen suggests</div>
            <CopilotStartersCard value={t.copilot_starters} onChange={(v) => saveConfig({ copilot_starters: v })} readOnly={!canEditStarters || !on} />
          </div>
        </div>
      )}
    </div>
  );
}

// Platform-level dials (super admin only; the API ignores them for anyone else).
function CopilotDials({ t, on, saveConfig }) {
  const access = { managers: t.copilot_access?.managers !== false, board: t.copilot_access?.board !== false };
  return (<>
    <div className={`rounded-2xl border border-sand-200 bg-sand-50/60 p-4 ${!on ? "opacity-60" : ""}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-semibold text-sm"><Mic className="h-4 w-4 text-ink/60" strokeWidth={2} /> Voice</div>
          <div className="text-xs text-muted-foreground mt-1">
            <span className="font-semibold text-ink">Dictation only</span> adds a microphone to the composer — speak, Whisper transcribes, the text lands in the box for review.{" "}
            <span className="font-semibold text-ink">Full conversation</span> adds a live spoken exchange whose turns land in the same chat thread; it can read documents and modules out loud but never changes anything, runs on this {V.lc("org")}'s OpenAI voice key, and ends at ten minutes.
            {!on && <span className="block mt-1 text-amber-700 font-medium">Turn Ask Copilot on first.</span>}
          </div>
        </div>
        <select data-testid="copilot-voice-mode" disabled={!on} value={voiceModeOf(t)} onChange={(e) => saveConfig({ copilot_voice_mode: e.target.value })}
          className="w-full sm:w-40 shrink-0 sm:mt-1 rounded-xl bg-white border border-sand-200 px-3 py-2.5 text-sm outline-none focus:border-ink disabled:opacity-60">
          <option value="off">Off</option>
          <option value="dictation">Dictation only</option>
          <option value="conversation">Full conversation</option>
        </select>
      </div>
    </div>
    <div className={`rounded-2xl border border-sand-200 bg-sand-50/60 p-4 ${!on ? "opacity-60" : ""}`} data-testid="copilot-listener-block">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 font-semibold text-sm"><Lightbulb className="h-4 w-4 text-ink/60" strokeWidth={2} /> Feature requests to Redline</div>
          <div className="text-xs text-muted-foreground mt-1">When Copilot is asked for something Ask Redline can't do yet, it says so and offers to send the request to the Ask Redline team; managers also get a <span className="font-semibold text-ink">Suggest a feature</span> button with an optional voice note. Off hides both and nothing is sent.</div>
        </div>
        <label className="inline-flex items-center gap-2.5 cursor-pointer shrink-0 mt-1">
          <Switch data-testid="copilot-listener-toggle" disabled={!on} checked={t.copilot_listener_enabled !== false} onCheckedChange={(v) => saveConfig({ copilot_listener_enabled: v })} />
          <span className="text-sm font-semibold text-ink">{t.copilot_listener_enabled !== false ? "On" : "Off"}</span>
        </label>
      </div>
    </div>
    <div className={`rounded-2xl border border-sand-200 p-4 ${!on ? "opacity-60" : ""}`} data-testid="copilot-access-block">
      <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-muted-foreground mb-1">Who can use Ask Copilot</div>
      <div className="flex items-center justify-between gap-4 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm">{V.one("org")} managers</div>
          <div className="text-xs text-muted-foreground">Managers can open and query the Copilot workspace.</div>
        </div>
        <Switch data-testid="copilot-toggle-managers" disabled={!on} checked={access.managers} onCheckedChange={(v) => saveConfig({ copilot_access: { ...access, managers: v } })} />
      </div>
      <div className="flex items-center justify-between gap-4 py-2.5 border-t border-sand-100">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm">{V.one("org_short")} {V.lcs("board")}</div>
          <div className="text-xs text-muted-foreground">{V.many("board")} can open and query the Copilot workspace.</div>
        </div>
        <Switch data-testid="copilot-toggle-board" disabled={!on} checked={access.board} onCheckedChange={(v) => saveConfig({ copilot_access: { ...access, board: v } })} />
      </div>
    </div>
  </>);
}
